import { Playlist, Curated, PlaylistProvider } from "../models";
import { APIVersion, AuthenticatedRequest, HTTPMethod, AuthenticatedFileRequest } from "../requests/request";

export class API {

    /**
     * Fetches the curated sets that are followed by the user
     * @returns {Promise<Curated[]>} The Promise contains an APIError if rejected
     */
    public static getFollowedCuratedSets(): Promise<Curated[]> {
        let apiRequest = new AuthenticatedRequest<Curated>('/followedsets', HTTPMethod.GET, APIVersion.v3_0);

        return apiRequest.send(Curated).then((response) => {
            return <Curated[]>response.data;
        });
    }

    /**
     * Follows the curated set
     * id - string
     * @returns {Promise<void>} The Promise contains an APIError if rejected
     */
    public static followCuratedSet(id: string): Promise<void> {
        let apiRequest = new AuthenticatedRequest<void>('/followedsets/' + id, HTTPMethod.POST, APIVersion.v3_0);

        return apiRequest.send().then((response) => {
            return void 0;
        });
    }

    /**
     * Follows the curated set
     * id - string
     * @returns {Promise<void>} The Promise contains an APIError if rejected
     */
    public static unfollowCuratedSet(id: string): Promise<void> {
        let apiRequest = new AuthenticatedRequest<void>('/followedsets/' + id, HTTPMethod.DELETE, APIVersion.v3_0);

        return apiRequest.send().then((response) => {
            return void 0;
        });
    }

    /**
     * Fetches the platforms playlist providers
     * @returns {Promise<Playlist[]>} The Promise contains an APIError if rejected
     */
    public static getPlaylistProviders(): Promise<PlaylistProvider[]> {
        let apiRequest = new AuthenticatedRequest<PlaylistProvider>('/playlist-providers', HTTPMethod.GET, APIVersion.v3_0);

        return apiRequest.send(PlaylistProvider).then((response) => {
            return <PlaylistProvider[]>response.data;
        });
    }

    /**
     * Fetches the users's playlists
     * @returns {Promise<Playlist[]>} The Promise contains an APIError if rejected
     */
    public static getPlaylists(): Promise<Playlist[]> {
        let apiRequest = new AuthenticatedRequest<Playlist>('/playlists', HTTPMethod.GET, APIVersion.v3_0);
        apiRequest.preventInvalidSubscriptionHandler = true
        return apiRequest.send(Playlist).then((response) => {
            return <Playlist[]>response.data;
        });
    }

    /**
     * Fetches a playlist by id
     * @returns {Promise<Playlist>} The Promise contains an APIError if rejected
     */
    public static getPlaylist(playlist_id: string): Promise<Playlist> {
        let apiRequest = new AuthenticatedRequest<Playlist>('/playlists/' + playlist_id, HTTPMethod.GET, APIVersion.v3_0);

        return apiRequest.send(Playlist).then((response) => {
            return <Playlist>response.data;
        });
    }

    /**
     * Creates a playlist
     * title - string
     * songs - An array of the track's id's
     * @returns {Promise<Playlist>} The Promise contains an APIError if rejected
     */
    public static createPlaylist(title: string, songs: string[]): Promise<Playlist> {
        let apiRequest = new AuthenticatedRequest<Playlist>('/playlists/', HTTPMethod.POST, APIVersion.v3_0, undefined, {
            title: title,
            songs: songs
        });

        return apiRequest.send(Playlist).then((response) => {
            return <Playlist>response.data;
        });
    }

    /**
     * Updates a playlist
     * title - Updates the title
     * add_songs - Songs to add to the playlist
     * remove_songs - Songs to remove from the playlist
     * @returns {Promise<Playlist>} The Promise contains an APIError if rejected
     */
    public static updatePlaylist(playlist_id: string, title?: string, add_songs?: string[], remove_songs?: string[]): Promise<Playlist> {
        let apiRequest = new AuthenticatedRequest<Playlist>('/playlists/' + playlist_id, HTTPMethod.PUT, APIVersion.v3_0, undefined, {
            title: title,
            add_songs: add_songs,
            delete_songs: remove_songs
        });

        return apiRequest.send(Playlist).then((response) => {
            return <Playlist>response.data;
        });
    }

    /**
     * Deletes a playlist
     * @returns {Promise<Playlist>} The Promise contains an APIError if rejected
     */
    public static deletePlaylist(playlist_id: string): Promise<void> {
        let apiRequest = new AuthenticatedRequest('/playlists/' + playlist_id, HTTPMethod.DELETE, APIVersion.v3_0);

        return apiRequest.send().then(() => {
            return void 0;
        });
    }

    /**
     * Uploads a new image for the playlist
     * @param {string} image - The uri for the image to set
     * @returns {Promise<User>} The Promise contains an APIError if rejected
     */
    public static uploadPlaylistImage(playlist_id: string, image: any): Promise<Playlist> {
        let apiRequest = new AuthenticatedFileRequest<Playlist>('/playlists/' + playlist_id + '/picture', HTTPMethod.POST, APIVersion.v3_0, undefined, {
            image: image
        });
        return apiRequest.send(Playlist).then((response) => {
            return <Playlist>response.data;
        });
    }

}



// WEBPACK FOOTER //
// ./src/api/src/endpoints/playlist.ts